import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Power, MessageSquare, Save, ArrowLeftRight, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { FadeUp } from './Motion';

const Toggle = ({ enabled, onChange, testId }) => (
  <button onClick={onChange} data-testid={testId}
    className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}>
    <motion.span layout transition={{ type: 'spring', stiffness: 500, damping: 32 }}
      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md ${enabled ? 'left-7' : 'left-1'}`} />
  </button>
);

export const AdminSettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => { setSettings(r.data); setMsg(r.data.closed_message || ''); }).catch(() => toast.error('Erreur de chargement'));
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await api.put('/admin/settings', { [key]: next[key] });
      toast.success(next[key] ? 'Canal ouvert ✓' : 'Canal fermé');
    } catch { toast.error('Erreur'); setSettings(settings); }
  };

  const saveMessage = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { closed_message: msg });
      toast.success('Message enregistré ✓');
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  if (!settings) return <div className="bg-white rounded-3xl p-10 text-center text-gray-400 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">Chargement...</div>;

  const channels = [
    { key: 'exchange_enabled', icon: ArrowLeftRight, title: 'Échange classique', desc: "Widget d'échange crypto ↔ fiat (achat & vente)" },
    { key: 'p2p_enabled', icon: Users, title: 'Trading Direct P2P', desc: 'Carnet d\'ordres entre clients et contrepartie alphaswapp' },
  ];

  return (
    <FadeUp className="space-y-6" data-testid="admin-settings-section">
      <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        <h2 className="font-extrabold text-2xl text-gray-900 mb-1 tracking-tight flex items-center space-x-2" style={{ fontFamily: 'Manrope' }}>
          <Power className="w-5 h-5 text-gray-400" /><span>Canaux d'échange</span>
        </h2>
        <p className="text-sm text-gray-500 mb-6">Ouvrez ou fermez les opérations selon la disponibilité de vos liquidités. Un canal fermé bloque toute nouvelle transaction et affiche votre message personnalisé.</p>
        <div className="space-y-3">
          {channels.map(({ key, icon: Icon, title, desc }) => (
            <div key={key} className={`flex items-center justify-between p-5 rounded-2xl border transition-colors ${settings[key] ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`} data-testid={`channel-${key}`}>
              <div className="flex items-center space-x-4">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${settings[key] ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                  <Icon className={`w-5 h-5 ${settings[key] ? 'text-emerald-600' : 'text-rose-600'}`} />
                </div>
                <div>
                  <div className="font-bold text-gray-900">{title}</div>
                  <div className="text-xs text-gray-500">{desc}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${settings[key] ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {settings[key] ? 'OUVERT' : 'FERMÉ'}
                </span>
                <Toggle enabled={settings[key]} onChange={() => toggle(key)} testId={`toggle-${key}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        <h2 className="font-extrabold text-xl text-gray-900 mb-1 tracking-tight flex items-center space-x-2" style={{ fontFamily: 'Manrope' }}>
          <MessageSquare className="w-5 h-5 text-gray-400" /><span>Message personnalisé (chablon)</span>
        </h2>
        <p className="text-sm text-gray-500 mb-4">Affiché aux clients sur les fenêtres d'échange lorsque le canal est fermé ou une transaction refusée.</p>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={500}
          className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-4 outline-none text-gray-900 resize-none focus:ring-2 focus:ring-gray-900/10"
          placeholder="Ex : Les échanges sont temporairement suspendus (maintenance)..." data-testid="closed-message-input" />
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-400 font-mono-data">{msg.length}/500</span>
          <button onClick={saveMessage} disabled={saving} className="btn-pill-dark px-6 py-2.5 text-sm flex items-center space-x-2 disabled:opacity-50" data-testid="save-message-btn">
            <Save className="w-4 h-4" /><span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
          </button>
        </div>
      </div>
    </FadeUp>
  );
};

export default AdminSettingsPanel;
